/* eslint-disable import/no-extraneous-dependencies */
import { defineComponent, onMounted, ref, watch } from 'vue';
import {
  getEditorEmits,
  getColorPickerProps,
  useNamespace,
  useUIStore,
} from '@ibiz-template/vue3-util';
import Alwan from 'alwan';
import 'alwan/css';
import { createUUID } from 'qx-util';
import './ibiz-color-picker.scss';
import { ColorPickerEditorController } from '../color-picker-editor.controller';

/**
 * 移动端颜色选择器
 * @primary
 * @description  使用van-field组件，轻量化的颜色选择器，支持色板选取、RGB 格式输入及实时预览，满足高效精准的色彩配置需求。支持编辑器类型包含：`移动端颜色选择器`
 * @editorparams {name:readonly,parameterType:boolean,defaultvalue:false,description:设置编辑器是否为只读态}
 * @editorparams {"name":"coloroptions","parameterType":"IApiData","description":"alwan颜色选项，具体参考https://github.com/sefianecho/alwan"}
 * @editorparams {name:colorformat,parameterType:string,defaultvalue:hex,description:颜色格式，支持rgb、hsl、hex}
 * @ignoreprops  autoFocus | overflowMode
 * @ignoreemits  infoTextChange | enter
 */
export const IBizColorPicker = defineComponent({
  name: 'IBizColorPicker',
  props: getColorPickerProps<ColorPickerEditorController>(),
  emits: getEditorEmits(),
  setup(props, { emit }) {
    const ns = useNamespace('color-picker');

    const c = props.controller;

    const currentVal = ref<string | null>('');

    const { UIStore } = useUIStore();

    const uuid = createUUID();

    const predefineColors = ref([
      '#000000',
      '#2C2C2C',
      '#50555C',
      '#ACB3BF',
      '#D0D3D9',
      '#C4C4C4',
      '#DADADA',
      '#E5E5E5',
      '#F0F0F0',
      '#F24E1E',
      '#E99C58',
      '#FFC700',
      '#FF4D00',
      '#FF00D6',
      '#D82E57',
      '#8E1DE8',
      '#0ACF83',
      '#18A0FB',
      '#A259FF',
      '#907CFF',
    ]);

    watch(
      () => props.value,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          if (!newVal) {
            currentVal.value = '';
          } else {
            currentVal.value = newVal;
          }
        }
      },
      { immediate: true },
    );

    const handleChange = (e: IData) => {
      if (!e) {
        return;
      }
      const { target } = e;
      if (!target) {
        return;
      }
      const { value } = target;
      emit('change', value);
    };

    const inputRef = ref();

    // 聚焦
    const onFocus = () => {
      emit('focus');
    };

    // 失焦
    const onBlur = () => {
      emit('blur');
    };

    onMounted(() => {
      c.color = new Alwan(`#alwan${uuid}`, {
        theme: UIStore.theme as 'light' | 'dark',
        color: currentVal.value || '',
        swatches: predefineColors.value,
        ...c.colorOptions,
      });
      c.color.on('change', (event: IData) => {
        emit('change', event[c.colorFormat]);
      });
    });

    return {
      ns,
      c,
      uuid,
      currentVal,
      handleChange,
      inputRef,
      onFocus,
      onBlur,
    };
  },
  render() {
    let content = null;
    if (this.readonly) {
      // 只读显示
      content = `${this.currentVal || ''}`;
    } else {
      // 编辑态显示
      content = [
        <van-field
          class={[this.ns.e('item'), this.ns.e('color')]}
          v-model={this.currentVal}
          disabled={this.disabled}
          placeholder={this.c.placeHolder}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          readonly
        ></van-field>,
        <div id={`alwan${this.uuid}`}></div>,
      ];
    }

    return (
      <div
        class={[
          this.ns.b(),
          this.disabled ? this.ns.m('disabled') : '',
          this.readonly ? this.ns.m('readonly') : '',
        ]}
        style={{ color: this.currentVal || '' }}
      >
        {content}
      </div>
    );
  },
});
